import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

const unwrapLookups = (payload) => {
    const nested = payload?.results?.data || payload?.data || payload;
    return {
        departments: Array.isArray(nested?.departments)
            ? nested.departments
            : Array.isArray(payload?.departments)
            ? payload.departments
            : [],
        nodeStatuses: Array.isArray(nested?.node_statuses)
            ? nested.node_statuses
            : Array.isArray(payload?.node_statuses)
            ? payload.node_statuses
            : [],
    };
};

const today = () => {
    // date_of_production is chosen by the operator on the phone, so the plain
    // calendar day is the right default; no shift bucketing applies.
    const now = new Date();
    const pad = (value) => String(value).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const formatTimestamp = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString();
};

const buildColumns = (blinkNameByValue) => [
    {
        accessor: "machine_name",
        header: "Machine",
        sortable: true,
    },
    {
        accessor: "department_name",
        header: "Department",
        sortable: true,
        cell: (row) => row.department_name || "—",
    },
    {
        accessor: "date_of_production",
        header: "Date of Production",
        sortable: true,
        cell: (row) => row.date_of_production || "—",
    },
    {
        accessor: "logged_at",
        header: "Logged At",
        sortable: true,
        cell: (row) => formatTimestamp(row.logged_at),
    },
    {
        accessor: "production_count",
        header: "Production Count",
        sortable: true,
        // An operator entering 0 is data, not an absent reading.
        cell: (row) => row.production_count ?? 0,
    },
    {
        accessor: "machine_on",
        header: "Machine On",
        sortable: true,
        cell: (row) => (row.machine_on === false ? "Off" : "On"),
    },
    {
        accessor: "node_on",
        header: "Node On",
        sortable: true,
        cell: (row) => (row.node_on === false ? "Off" : "On"),
    },
    {
        accessor: "node_status",
        header: "Blink",
        sortable: true,
        cell: (row) => {
            if (!row.node_status) return "—";
            return blinkNameByValue[row.node_status] || row.node_status;
        },
    },
    {
        accessor: "created_by_name",
        header: "Created By",
        sortable: false,
        cell: (row) => row.created_by_name || "—",
    },
];

const FloorData = () => {
    const { apiClient } = useGlobalAPIQueryContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [entries, setEntries] = useState(
        Number(searchParams.get("entries")) || 10
    );
    const [pageIndex, setPageIndex] = useState(
        (Number(searchParams.get("page")) || 1) - 1
    );
    const [globalFilterValue, setGlobalFilterValue] = useState(
        searchParams.get("q") || ""
    );
    const [departmentId, setDepartmentId] = useState(
        searchParams.get("department_id") || ""
    );
    const [machineOn, setMachineOn] = useState(
        searchParams.get("machine_on") || ""
    );
    const [nodeOn, setNodeOn] = useState(searchParams.get("node_on") || "");
    const [nodeStatus, setNodeStatus] = useState(
        searchParams.get("node_status") || ""
    );
    const [fromDate, setFromDate] = useState(
        searchParams.get("from_date") || today()
    );
    const [toDate, setToDate] = useState(searchParams.get("to_date") || today());
    const [sorting, setSorting] = useState([]);
    const [isExporting, setIsExporting] = useState(false);

    const { useFetch: useFetchLookups } = useAPIQuery({
        key: "api/floor-data/lookups",
        endpoint: "/api/floor-data/lookups/",
    });
    const { data: lookupResponse } = useFetchLookups({
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });
    const lookups = useMemo(
        () => unwrapLookups(lookupResponse),
        [lookupResponse]
    );
    const departments = lookups.departments;
    const nodeStatuses = lookups.nodeStatuses;
    const blinkNameByValue = useMemo(() => {
        const map = {};
        nodeStatuses.forEach((row) => {
            if (row?.value) map[row.value] = row.name || row.value;
        });
        return map;
    }, [nodeStatuses]);
    const columns = useMemo(
        () => buildColumns(blinkNameByValue),
        [blinkNameByValue]
    );

    const sortParams = useMemo(
        () => ({
            sort_q:
                sorting.map((item) => (item?.desc ? "desc" : "asc")).join(",") ||
                undefined,
            sort_c: sorting.map((item) => item?.id).join(",") || undefined,
        }),
        [sorting]
    );

    const queryParams = useMemo(
        () => ({
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            q: globalFilterValue || undefined,
            department_id: departmentId || undefined,
            machine_on: machineOn || undefined,
            node_on: nodeOn || undefined,
            node_status: nodeStatus || undefined,
        }),
        [
            fromDate,
            toDate,
            globalFilterValue,
            departmentId,
            machineOn,
            nodeOn,
            nodeStatus,
        ]
    );

    const { useFetch } = useAPIQuery({
        key: "api/floor-data",
        endpoint: "/api/floor-data/",
    });

    const { data, isLoading, isFetching } = useFetch({
        params: {
            ...queryParams,
            ...sortParams,
            page_no: pageIndex + 1,
            entries,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 30,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const rows = Array.isArray(data?.results?.data)
        ? data.results.data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];

    const updateParams = (mutate) => {
        setPageIndex(0);
        setSearchParams((params) => {
            mutate(params);
            params.set("page", "1");
            return params;
        });
    };

    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        setGlobalFilterValue(q);
        updateParams((params) => {
            if (q) params.set("q", q);
            else params.delete("q");
        });
    };

    const handleDepartmentChange = (value) => {
        setDepartmentId(value);
        updateParams((params) => {
            if (value) params.set("department_id", value);
            else params.delete("department_id");
        });
    };

    const handleMachineOnChange = (value) => {
        setMachineOn(value);
        updateParams((params) => {
            if (value) params.set("machine_on", value);
            else params.delete("machine_on");
        });
    };

    const handleNodeOnChange = (value) => {
        setNodeOn(value);
        updateParams((params) => {
            if (value) params.set("node_on", value);
            else params.delete("node_on");
        });
    };

    const handleNodeStatusChange = (value) => {
        setNodeStatus(value);
        updateParams((params) => {
            if (value) params.set("node_status", value);
            else params.delete("node_status");
        });
    };

    const handleFromDateChange = (value) => {
        setFromDate(value);
        updateParams((params) => {
            if (value) params.set("from_date", value);
            else params.delete("from_date");
        });
    };

    const handleToDateChange = (value) => {
        setToDate(value);
        updateParams((params) => {
            if (value) params.set("to_date", value);
            else params.delete("to_date");
        });
    };

    const handlePagination = (value) => {
        setSearchParams((params) => {
            params.set("entries", value.pageSize);
            params.set("page", value.pageIndex + 1);
            return params;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const response = await apiClient.get("/api/floor-data/", {
                params: {
                    ...queryParams,
                    ...sortParams,
                    export_format: "xlsx",
                },
                responseType: "blob",
            });
            const contentDisposition = response.headers["content-disposition"];
            const filename =
                contentDisposition
                    ?.split("filename=")?.[1]
                    ?.replaceAll('"', "")
                    ?.trim() || "floor_data_log.xlsx";
            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(href);
        } catch (error) {
            Swal.fire({
                title: "Download failed",
                text:
                    error?.response?.data?.results?.message ||
                    error.message ||
                    "Unable to export floor data",
                icon: "error",
            });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between flex-wrap g-3">
                    <div className="nk-block-head-content">
                        <h3 className="nk-block-title page-title">
                            Floor Data
                        </h3>
                        <p className="text-soft">
                            Production entries logged by operators from the
                            mobile app, with who submitted each one and when.
                        </p>
                    </div>
                    <div className="nk-block-head-content">
                        <button
                            type="button"
                            id="floor-data-export"
                            className="btn btn-primary"
                            onClick={handleExport}
                            disabled={isExporting}
                        >
                            <em className="icon ni ni-download" />
                            <span>
                                {isExporting
                                    ? "Exporting…"
                                    : "Export Excel"}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="card card-bordered mb-3">
                <div className="card-inner">
                    <div className="row g-3 align-items-end">
                        <div className="col-6 col-md-4 col-xl-2">
                            <div className="form-group mb-0">
                                <label className="form-label" htmlFor="floor-from-date">
                                    From
                                </label>
                                <input
                                    id="floor-from-date"
                                    type="date"
                                    className="form-control"
                                    value={fromDate}
                                    max={toDate || undefined}
                                    onChange={(event) =>
                                        handleFromDateChange(event.target.value)
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-6 col-md-4 col-xl-2">
                            <div className="form-group mb-0">
                                <label className="form-label" htmlFor="floor-to-date">
                                    To
                                </label>
                                <input
                                    id="floor-to-date"
                                    type="date"
                                    className="form-control"
                                    value={toDate}
                                    min={fromDate || undefined}
                                    onChange={(event) =>
                                        handleToDateChange(event.target.value)
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-6 col-md-4 col-xl-2">
                            <div className="form-group mb-0">
                                <label
                                    className="form-label"
                                    htmlFor="floor-department"
                                >
                                    Department
                                </label>
                                <select
                                    id="floor-department"
                                    className="form-control"
                                    value={departmentId}
                                    onChange={(event) =>
                                        handleDepartmentChange(
                                            event.target.value
                                        )
                                    }
                                >
                                    <option value="">All departments</option>
                                    {departments.map((row) => (
                                        <option key={row.id} value={row.id}>
                                            {row.department_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-6 col-md-4 col-xl-2">
                            <div className="form-group mb-0">
                                <label
                                    className="form-label"
                                    htmlFor="floor-machine-on"
                                >
                                    Machine On
                                </label>
                                <select
                                    id="floor-machine-on"
                                    className="form-control"
                                    value={machineOn}
                                    onChange={(event) =>
                                        handleMachineOnChange(
                                            event.target.value
                                        )
                                    }
                                >
                                    <option value="">All</option>
                                    <option value="true">On</option>
                                    <option value="false">Off</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-6 col-md-4 col-xl-2">
                            <div className="form-group mb-0">
                                <label
                                    className="form-label"
                                    htmlFor="floor-node-on"
                                >
                                    Node On
                                </label>
                                <select
                                    id="floor-node-on"
                                    className="form-control"
                                    value={nodeOn}
                                    onChange={(event) =>
                                        handleNodeOnChange(event.target.value)
                                    }
                                >
                                    <option value="">All</option>
                                    <option value="true">On</option>
                                    <option value="false">Off</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-6 col-md-4 col-xl-2">
                            <div className="form-group mb-0">
                                <label
                                    className="form-label"
                                    htmlFor="floor-blink"
                                >
                                    Blink
                                </label>
                                <select
                                    id="floor-blink"
                                    className="form-control"
                                    value={nodeStatus}
                                    onChange={(event) =>
                                        handleNodeStatusChange(
                                            event.target.value
                                        )
                                    }
                                >
                                    <option value="">All</option>
                                    {nodeStatuses.map((row) => (
                                        <option
                                            key={row.value}
                                            value={row.value}
                                        >
                                            {row.name || row.value}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Table
                data={rows}
                columns={columns}
                pagination
                pageIndex={pageIndex}
                pageSize={entries}
                rowCount={
                    typeof data?.count === "number" ? data.count : rows.length
                }
                globalSearch
                isLoading={isLoading || isFetching}
                editRows={false}
                deleteRows={false}
                filterModal={false}
                selectRows={false}
                defaultGlobalSearchValue={globalFilterValue || undefined}
                onGlobalFilterChange={handleGlobalFilterChange}
                onPaginationChange={handlePagination}
                onSortingChange={setSorting}
            />
        </>
    );
};

export default FloorData;
